import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { RealMapComponent } from '../../../components/map';
import { api } from '../../../services/api';
import type { Device, MapMarker, Coordinates } from '../../../types';

interface DeviceMapTabProps {
  // Пока без пропов, но может понадобиться для коммуникации между табами
}

export const DeviceMapTab: React.FC<DeviceMapTabProps> = () => {
  const { t } = useTranslation();
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  // Запрос устройств
  const {
    data: devicesData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['devices-map'],
    queryFn: () => api.getDevices({
      page: 1,
      size: 1000, // Получаем все устройства для карты
    }),
    refetchInterval: 60000, // Обновление каждую минуту
  });

  const devices = devicesData?.items || [];

  // Фильтрация устройств с координатами
  const devicesWithCoordinates = useMemo(() => {
    return devices.filter(device => 
      device.latitude != null && 
      device.longitude != null &&
      !isNaN(device.latitude) &&
      !isNaN(device.longitude)
    );
  }, [devices]);

  // Преобразование устройств в маркеры для карты
  const markers: MapMarker[] = useMemo(() => {
    return devicesWithCoordinates.map(device => ({
      id: device.id,
      name: device.name,
      coordinates: {
        latitude: device.latitude!,
        longitude: device.longitude!
      },
      device,
      tooltip: `${device.name}\n${device.status} • ${device.type || device.device_type}`
    }));
  }, [devicesWithCoordinates]);

  // Статистика устройств
  const stats = useMemo(() => {
    const total = devices.length;
    const withCoordinates = devicesWithCoordinates.length;
    const online = devicesWithCoordinates.filter(d => d.status === 'online').length;
    const offline = devicesWithCoordinates.filter(d => d.status === 'offline').length;
    const error = devicesWithCoordinates.filter(d => d.status === 'error').length;

    return { total, withCoordinates, online, offline, error };
  }, [devices, devicesWithCoordinates]);

  // Обработка клика по маркеру
  const handleMarkerClick = (marker: MapMarker) => {
    if (marker.device) {
      setSelectedDevice(marker.device);
    }
  };

  // Обработка обновления данных
  const handleRefresh = () => {
    refetch();
  };

  // Центральная точка карты (средние координаты всех устройств)
  const mapCenter: Coordinates | undefined = useMemo(() => {
    if (markers.length === 0) {
      return { latitude: 55.7558, longitude: 37.6176 }; // Москва по умолчанию
    }

    const avgLat = markers.reduce((sum, m) => sum + m.coordinates.latitude, 0) / markers.length;
    const avgLng = markers.reduce((sum, m) => sum + m.coordinates.longitude, 0) / markers.length;

    return { latitude: avgLat, longitude: avgLng };
  }, [markers]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        <AlertTitle>{t('devices.errorLoadingDevices')}</AlertTitle>
        {error?.message || t('common.unknownError')}
      </Alert>
    );
  }

  return (
    <Box sx={{ 
      height: {
        xs: 'calc(100vh - 200px)', 
        sm: 'calc(100vh - 220px)', 
        md: 'calc(100vh - 240px)'  
      },
      width: '100%',
      display: 'flex', 
      flexDirection: 'column',
      minHeight: {
        xs: '500px',
        sm: '600px', 
        md: '700px'
      }
    }}>
      {/* Заголовок и статистика */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('map.title')}
          </Typography>
          
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Статистика */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={1} 
          flexWrap="wrap"
          sx={{ gap: { xs: 1, sm: 1 } }}
        >
          <Chip 
            label={`${t('devices.totalDevices')}: ${stats.total}`}
            color="default"
            size="small"
          />
          <Chip 
            label={`${t('map.devices')}: ${stats.withCoordinates}`}
            color="primary"
            size="small"
          />
          <Chip 
            label={`${t('devices.status.online')}: ${stats.online}`}
            color="success"
            size="small"
          />
          <Chip 
            label={`${t('devices.status.offline')}: ${stats.offline}`}
            color="default"
            size="small"
          />
          {stats.error > 0 && (
            <Chip 
              label={`${t('devices.status.error')}: ${stats.error}`}
              color="error"
              size="small"
            />
          )}
        </Stack>

        {stats.withCoordinates === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>{t('map.noDevicesOnMap')}</AlertTitle>
            {t('devices.noDevicesWithCoordinates')}
          </Alert>
        )}
      </Paper>

              {/* Основной контент */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, // На мобильных - колонка, на десктопе - строка
          flex: 1, 
          gap: 2, 
          height: '100%',
          overflow: 'visible' // Разрешаем переполнение для полноширинной карты
        }}>
        {/* Карта */}
        <Paper 
          elevation={1} 
          sx={{ 
            flex: 1, 
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            height: '100%', // Занимаем всю доступную высоту
            width: '100%', // Занимаем всю доступную ширину
            minHeight: 0, // Позволяем сжиматься
            minWidth: 0 // Позволяем сжиматься по ширине
          }}
        >
          <RealMapComponent
            markers={markers}
            onMarkerClick={handleMarkerClick}
            selectedMarker={selectedDevice ? markers.find(m => m.device?.id === selectedDevice.id) || null : null}
            showControls={true}
            interactive={true}
            config={{
              center: mapCenter,
              zoom: markers.length > 1 ? 10 : 15
            }}
            height="100%" // Полная высота контейнера
          />
        </Paper>

        {/* Боковая панель с информацией об устройстве */}
        {selectedDevice && (
          <Box sx={{ 
            width: { xs: '100%', md: 320 }, // Полная ширина на мобильных, фиксированная на десктопе
            height: { xs: 250, md: '100%' }, // Ограниченная высота на мобильных
            flexShrink: 0,
            overflow: 'auto' // Скролл если содержимое не помещается
          }}>
            <Card elevation={1}>
              <CardContent>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  mb: 2
                }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    {selectedDevice.name}
                  </Typography>
                  
                  {/* Кнопка закрытия (особенно полезна на мобильных) */}
                  <Tooltip title={t('common.close')}>
                    <IconButton 
                      size="small" 
                      onClick={() => setSelectedDevice(null)}
                      sx={{ ml: 1 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Stack spacing={2}>
                  {/* Статус */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('devices.status')}
                    </Typography>
                    <Chip
                      label={t(`devices.status.${selectedDevice.status}`)}
                      color={
                        selectedDevice.status === 'online' ? 'success' :
                        selectedDevice.status === 'error' ? 'error' : 'default'
                      }
                      size="small"
                    />
                  </Box>

                  {/* Тип устройства */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('devices.type')}
                    </Typography>
                    <Typography variant="body2">
                      {selectedDevice.type || selectedDevice.device_type}
                    </Typography>
                  </Box>

                  {/* Местоположение */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('devices.location')}
                    </Typography>
                    <Typography variant="body2">
                      {selectedDevice.full_address || 
                       (selectedDevice.city && selectedDevice.country 
                         ? `${selectedDevice.city}, ${selectedDevice.country}` 
                         : selectedDevice.location || t('common.notSpecified'))}
                    </Typography>
                    {(selectedDevice.floor || selectedDevice.room) && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {[selectedDevice.floor && `${t('devices.floor')}: ${selectedDevice.floor}`,
                          selectedDevice.room && `${t('devices.room')}: ${selectedDevice.room}`
                        ].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>

                  {/* Координаты */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('map.coordinates')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {selectedDevice.latitude?.toFixed(6)}, {selectedDevice.longitude?.toFixed(6)}
                    </Typography>
                  </Box>

                  {/* Последняя активность */}
                  {selectedDevice.last_heartbeat && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('devices.lastSeen')}
                      </Typography>
                      <Typography variant="body2">
                        {new Date(selectedDevice.last_heartbeat).toLocaleString()}
                      </Typography>
                    </Box>
                  )}

                  {/* Описание */}
                  {selectedDevice.description && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('devices.description')}
                      </Typography>
                      <Typography variant="body2">
                        {selectedDevice.description}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
}; 